import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import type { ReportWithRole } from '@/types'

export default function DashboardPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [reports, setReports] = useState<ReportWithRole[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetch('/api/reports/mine', { credentials: 'include' })
      .then(res => res.json())
      .then(result => {
        const list = result.reports || []
        setReports(list)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleCreateReport = async (year: string) => {
    setCreating(true)
    try {
      const res = await fetch(`/api/reports/current?year=${year}`, { credentials: 'include' })
      const result = await res.json()
      if (result.report?.reportId) {
        navigate(`/report/${result.report.reportId}`)
      }
    } catch {
      // ignore
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f5fc' }}>
        <p style={{ color: '#7c4dba', fontSize: 18, fontFamily: "'Georgia', serif" }}>Loading...</p>
      </div>
    )
  }

  const roleBadgeColors: Record<string, { bg: string; text: string }> = {
    minister: { bg: '#7c4dba', text: '#fff' },
    sponsor: { bg: '#d4a853', text: '#fff' },
    mentor: { bg: '#4a8c6f', text: '#fff' },
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f5fc', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{
        padding: '24px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(180,160,210,0.2)',
        background: 'rgba(255,255,255,0.6)',
      }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: '#9a80c0', marginBottom: 2 }}>
            Centers for Spiritual Living
          </div>
          <h1 style={{ margin: 0, fontFamily: "'Georgia', serif", color: '#2d1b4e', fontSize: 22, fontWeight: 400 }}>
            Minister's Report
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#7a6a8a', fontSize: 14 }}>{user?.email}</span>
          <button onClick={logout} style={{
            padding: '8px 20px', borderRadius: 16, border: '1px solid #d4c5e8',
            background: 'transparent', color: '#7c4dba', cursor: 'pointer', fontSize: 13, fontWeight: 500,
          }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 20px' }}>
        <h2 style={{ color: '#2d1b4e', fontFamily: "'Georgia', serif", fontWeight: 400, fontSize: 24, margin: '0 0 8px' }}>
          Your Reports
        </h2>
        <p style={{ color: '#7a6a8a', fontSize: 14, margin: '0 0 32px' }}>
          Select a report to view or edit, or create a new one.
        </p>

        {reports.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '48px 32px',
            background: 'rgba(255,255,255,0.6)', borderRadius: 20,
            border: '1px solid rgba(180,160,210,0.2)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
            <h3 style={{ color: '#2d1b4e', fontFamily: "'Georgia', serif", fontSize: 20, marginBottom: 12 }}>
              No Reports Found
            </h3>
            <p style={{ color: '#5a4a6a', fontSize: 15, margin: '0 0 24px', lineHeight: 1.6 }}>
              We couldn't find any reports associated with your email address.<br />
              If you believe this is an error, please contact:<br />
              <a href="mailto:charlene@charsoft.com" style={{ color: '#7c4dba', fontWeight: 600 }}>charlene@charsoft.com</a>
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 16 }}>
              <button onClick={() => handleCreateReport('2026')} disabled={creating} style={{
                padding: '14px 40px', borderRadius: 30, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
                fontSize: 16, fontWeight: 600, boxShadow: '0 4px 15px rgba(124,77,186,0.3)',
                opacity: creating ? 0.7 : 1,
              }}>
                {creating ? 'Creating...' : 'Start 2026 Report'}
              </button>
              <button onClick={() => handleCreateReport('2025')} disabled={creating} style={{
                padding: '14px 30px', borderRadius: 30, border: '2px solid #d4c5e8', cursor: 'pointer',
                background: 'transparent', color: '#7c4dba',
                fontSize: 15, fontWeight: 600,
                opacity: creating ? 0.7 : 1,
              }}>
                {creating ? 'Creating...' : 'Start 2025 Report'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {reports.map(report => {
              const badge = roleBadgeColors[report.role] || roleBadgeColors.minister
              return (
                <button
                  key={report.reportId}
                  onClick={() => navigate(`/report/${report.reportId}?role=${report.role}`)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px 24px', borderRadius: 16, border: '1px solid rgba(180,160,210,0.2)',
                    background: 'rgba(255,255,255,0.7)', cursor: 'pointer', textAlign: 'left',
                    transition: 'box-shadow 0.2s',
                    boxShadow: '0 2px 10px rgba(100,60,140,0.05)',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(100,60,140,0.12)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 2px 10px rgba(100,60,140,0.05)')}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ color: '#2d1b4e', fontSize: 16, fontWeight: 600 }}>
                        {report.reportYear} Report
                      </span>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: 1,
                        background: badge.bg, color: badge.text,
                      }}>
                        {report.role}
                      </span>
                    </div>
                    {report.role !== 'minister' && report.ministerName && (
                      <div style={{ color: '#7a6a8a', fontSize: 13 }}>
                        Minister: {report.ministerName}
                      </div>
                    )}
                    <div style={{ color: '#9a8aaa', fontSize: 12, marginTop: 4 }}>
                      Last updated: {report.updatedAt ? new Date(report.updatedAt).toLocaleDateString() : 'Never'}
                    </div>
                  </div>
                  <div style={{ color: '#b4a0d0', fontSize: 20 }}>→</div>
                </button>
              )
            })}

            {/* Show create buttons if the user doesn't have reports for those years */}
            {!reports.find(r => r.role === 'minister' && r.reportYear === '2026') && (
              <button onClick={() => handleCreateReport('2026')} disabled={creating} style={{
                padding: '16px 24px', borderRadius: 16, border: '2px dashed #7c4dba',
                background: 'rgba(124,77,186,0.05)', cursor: 'pointer', color: '#7c4dba',
                fontSize: 15, fontWeight: 600, opacity: creating ? 0.7 : 1,
              }}>
                {creating ? 'Creating...' : '+ Start Your 2026 Report'}
              </button>
            )}
            {!reports.find(r => r.role === 'minister' && r.reportYear === '2025') && (
              <button onClick={() => handleCreateReport('2025')} disabled={creating} style={{
                padding: '16px 24px', borderRadius: 16, border: '2px dashed #d4c5e8',
                background: 'transparent', cursor: 'pointer', color: '#9a80c0',
                fontSize: 15, fontWeight: 500, opacity: creating ? 0.7 : 1,
              }}>
                {creating ? 'Creating...' : '+ Start a 2025 Report'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
